import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { fmtEUR, categoryLabel } from "@/lib/constants";
import { CLIENT_NAV } from "@/lib/clientNav";
import RecurringMissionModal from "@/components/RecurringMissionModal";
import { LayoutDashboard, FilePlus, FileText, Briefcase, CreditCard, Users, Plus, Heart, ExternalLink, Repeat, UserX } from "lucide-react";
import ProfilePhoto from "@/components/ProfilePhoto";
import EvaluationModal from "@/components/EvaluationModal";
import DangerZone from "@/components/DangerZone";
import { ClientTimesheetValidationModal } from "@/components/TimeSheetModals";
import { CandidateBadges, CompatScore, SuperTalentBadge } from "@/components/CandidateBadges";
import ComparisonModal from "@/components/ComparisonModal";
import ClientFavorites from "@/components/ClientFavorites";
import ClientTeams from "@/components/ClientTeams";
import MissionChat from "@/components/MissionChat";
import { toast } from "sonner";

const NAV = CLIENT_NAV;

export default function ClientDashboard() {
    const [dash, setDash] = useState(null);

    useEffect(() => {
        api.get("/client/dashboard").then((r) => setDash(r.data)).catch(() => {});
    }, []);

    return (
        <DashboardShell title="Espace client" nav={NAV}>
            <div className="flex justify-between items-end flex-wrap gap-4 mb-8">
                <div>
                    <div className="overline mb-2">Bonjour</div>
                    <h1 className="font-display text-3xl md:text-4xl">
                        Vos missions en un coup d'œil.
                    </h1>
                </div>
                <Link to="/devis" className="btn-gold text-sm" data-testid="client-new-mission-btn">
                    <Plus className="inline -mt-1 mr-1" size={16} />
                    Créer une mission
                </Link>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <KPI label="Devis créés" value={dash?.quotes_count ?? "—"} />
                <KPI label="Missions" value={dash?.missions_count ?? "—"} />
                <KPI label="Dépense TTC" value={fmtEUR(dash?.spend_ttc ?? 0)} />
                <KPI label="Statut" value={dash?.missions_count ? "Actif" : "Nouveau"} />
            </div>

            <ClientRatingCard />
            <PaymentMethodBanner />
            <DeferredBillingCard />
            {/* Recent quotes */}
            <section className="sd-card p-6 mb-8">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <div className="overline">Devis récents</div>
                        <h2 className="font-display text-xl">Historique</h2>
                    </div>
                </div>
                <div className="divide-y divide-slate-100">
                    {(dash?.recent_quotes || []).length === 0 && (
                        <div className="py-6 text-sm text-slate-400 text-center">
                            Aucun devis pour le moment.
                        </div>
                    )}
                    {(dash?.recent_quotes || []).map((q) => (
                        <div
                            key={q.id}
                            className="py-4 flex items-center justify-between gap-3 flex-wrap"
                            data-testid={`quote-row-${q.id}`}
                        >
                            <div>
                                <div className="font-semibold">{q.mission_input?.title || "Mission"}</div>
                                <div className="text-xs text-slate-500">
                                    {q.quote_number} · {categoryLabel(q.mission_input?.job_category)}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className={`pill ${
                                    q.status === "paid" ? "pill-green" :
                                    q.status === "accepted" ? "pill-gold" :
                                    "pill-slate"
                                }`}>
                                    {q.status}
                                </span>
                                <span className="font-display font-bold">
                                    {fmtEUR(q.quote_result?.total_ttc)}
                                </span>
                                <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q.id}/pdf`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn-ghost-navy text-xs"
                                    data-testid={`quote-pdf-${q.id}`}
                                >
                                    PDF
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="sd-card p-6 mb-8">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <div className="overline">Contrats signés</div>
                        <h2 className="font-display text-xl">Vos contrats archivés</h2>
                    </div>
                </div>
                <ClientContractsList />
            </section>

            <PendingTimesheets />
            <PendingEvaluations />

            <section className="sd-card p-6">
                <div className="overline">Missions publiées</div>
                <h2 className="font-display text-xl mb-4">Missions actives</h2>
                <div className="divide-y divide-slate-100">
                    {(dash?.recent_missions || []).length === 0 && (
                        <div className="py-6 text-sm text-slate-400 text-center">
                            Aucune mission publiée. Réglez un devis pour publier votre première mission.
                        </div>
                    )}
                    {(dash?.recent_missions || []).map((m) => (
                        <ClientMissionRow key={m.id} m={m} />
                    ))}
                </div>
            </section>

            <div className="mt-8">
                <ClientTeams openMissions={(dash?.recent_missions || []).filter((m) => m.status === "open")} />
                <ClientFavorites />
            </div>

            <div className="mt-10">
                <DangerZone />
            </div>
        </DashboardShell>
    );
}

function ClientMissionRow({ m }) {
    const [apps, setApps] = useState([]);
    const [open, setOpen] = useState(false);
    const [compareIds, setCompareIds] = useState([]);
    const [showCompare, setShowCompare] = useState(false);
    const [showRecurring, setShowRecurring] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const refresh = () => {
        api.get(`/applications/mission/${m.id}`).then((r) => setApps(r.data)).catch(() => {});
    };
    useEffect(() => {
        if (open) refresh();
    }, [open, m.id]);
    const hasSelected = apps.some((a) => a.status === "selected");

    const toggleCompare = (candidateUserId) => {
        setCompareIds((cur) => {
            if (cur.includes(candidateUserId)) return cur.filter((x) => x !== candidateUserId);
            if (cur.length >= 3) {
                toast.error("Maximum 3 candidats à comparer");
                return cur;
            }
            return [...cur, candidateUserId];
        });
    };

    const duplicateMission = async () => {
        if (!window.confirm("Créer un nouveau devis à partir de cette mission ? Vous choisirez une nouvelle date et pourrez ajuster avant de payer.")) return;
        setDuplicating(true);
        try {
            const r = await api.post(`/client/missions/${m.id}/duplicate`);
            toast.success("Nouveau devis créé — vous serez redirigé vers le configurateur");
            setTimeout(() => { window.location.href = `/devis?edit=${r.data.quote_id}`; }, 800);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setDuplicating(false);
        }
    };

    return (
        <div className="py-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-semibold">{m.title}</div>
                    <div className="text-xs text-slate-500">
                        {m.city} ({m.postal_code}) · {m.date} · {m.people_count} pers.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`pill ${m.status === "open" ? "pill-gold" : "pill-green"}`}>
                        {m.status}
                    </span>
                    <button
                        onClick={duplicateMission}
                        disabled={duplicating}
                        className="btn-ghost-navy text-xs disabled:opacity-50"
                        title="Créer un nouveau devis identique (nouvelle date à choisir)"
                        data-testid={`duplicate-mission-${m.id}`}
                    >
                        {duplicating ? "…" : "Dupliquer"}
                    </button>
                    <button
                        onClick={() => setShowRecurring(true)}
                        className="btn-ghost-navy text-xs inline-flex items-center gap-1"
                        title="Publier automatiquement cette mission chaque semaine jusqu'à une date"
                        data-testid={`recurring-mission-${m.id}`}
                    >
                        <Repeat size={12} /> Récurrent
                    </button>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="btn-ghost-navy text-xs"
                        data-testid={`view-apps-${m.id}`}
                    >
                        {open ? "Masquer" : "Candidatures"}
                    </button>
                </div>
            </div>
            {open && (
                <div className="mt-3 pl-3 border-l-2 border-[#F5A623]/40 space-y-2">
                    {apps.length === 0 && (
                        <div className="text-xs text-slate-400 py-2">Aucune candidature pour l'instant.</div>
                    )}
                    {apps.length > 0 && (
                        <div className="flex items-center justify-between gap-2 pb-1">
                            <div className="text-[11px] text-slate-500">
                                {apps.filter((a) => !["rejected", "withdrawn"].includes(a.status)).length} / 7 candidatures actives · triées par compatibilité
                            </div>
                            {compareIds.length >= 2 && (
                                <button
                                    onClick={() => setShowCompare(true)}
                                    className="btn-gold text-[11px] py-1.5 px-3"
                                    data-testid={`open-comparison-${m.id}`}
                                >
                                    Comparer {compareIds.length} candidats
                                </button>
                            )}
                        </div>
                    )}
                    {apps.map((a) => (
                        <ClientAppRow
                            key={a.id}
                            a={a}
                            hasSelected={hasSelected}
                            onSelected={refresh}
                            selectedForCompare={compareIds.includes(a.candidate?.user_id)}
                            onToggleCompare={() => a.candidate?.user_id && toggleCompare(a.candidate.user_id)}
                        />
                    ))}
                </div>
            )}
            <ComparisonModal
                open={showCompare}
                candidateIds={compareIds}
                missionId={m.id}
                onClose={() => setShowCompare(false)}
            />
            {showRecurring && (
                <RecurringMissionModal
                    mission={m}
                    onClose={() => setShowRecurring(false)}
                    onDone={() => {
                        setShowRecurring(false);
                        refresh();
                    }}
                />
            )}
        </div>
    );
}

function ClientAppRow({ a, hasSelected, onSelected, selectedForCompare, onToggleCompare }) {
    const [showDocs, setShowDocs] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const [docs, setDocs] = useState([]);
    const [selecting, setSelecting] = useState(false);
    const [error, setError] = useState("");
    const [isFav, setIsFav] = useState(!!a.is_favorite);
    const isSelected = a.status === "selected";
    const isRejected = a.status === "rejected";
    useEffect(() => {
        if (showDocs && isSelected && a.candidate?.user_id) {
            api.get(`/client/candidates/${a.candidate.user_id}/documents`)
                .then((r) => setDocs(r.data))
                .catch(() => {});
        }
    }, [showDocs, isSelected, a.candidate?.user_id]);

    const toggleFavorite = async () => {
        const cid = a.candidate?.user_id;
        if (!cid) return;
        try {
            if (isFav) {
                await api.delete(`/client/favorites/${cid}`);
                setIsFav(false);
                toast.success("Retiré des favoris");
            } else {
                await api.post("/client/favorites", { candidate_user_id: cid });
                setIsFav(true);
                toast.success("Ajouté aux favoris");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };

    const downloadDoc = async (d) => {
        try {
            const resp = await api.get(
                `/candidates/documents/${d.id}/download`,
                { responseType: "blob" }
            );
            const url = URL.createObjectURL(resp.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = d.original_filename;
            link.click();
            URL.revokeObjectURL(url);
        } catch (_) {}
    };

    const downloadRecap = async () => {
        try {
            const resp = await api.get(
                `/applications/${a.id}/recap-pdf`,
                { responseType: "blob" }
            );
            const url = URL.createObjectURL(resp.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `dossier-prestataire-${a.candidate?.first_name || ""}-${a.candidate?.last_name || ""}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (_) {}
    };

    const pickCandidate = async () => {
        if (!window.confirm(
            "Confirmer la sélection de ce candidat ? Les autres candidatures seront automatiquement refusées et le dossier prestataire (KBIS, CNI, RIB, attestation de vigilance) vous sera envoyé par e-mail."
        )) return;
        setSelecting(true);
        setError("");
        try {
            await api.post(`/applications/${a.id}/select`);
            if (onSelected) onSelected();
        } catch (e) {
            setError(e?.response?.data?.detail || "Erreur lors de la sélection.");
        } finally {
            setSelecting(false);
        }
    };

    const STATUS_FR = { selected: "Sélectionné", rejected: "Refusé", pending: "En attente", preselected: "Présélectionné", withdrawn: "Retiré" };

    return (
        <div className={`rounded-lg p-3 ${isRejected ? "bg-slate-100 opacity-60" : "bg-slate-50"}`}>
            <div className="flex justify-between items-center gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    {!isSelected && !isRejected && a.candidate?.user_id && (
                        <input
                            type="checkbox"
                            checked={!!selectedForCompare}
                            onChange={onToggleCompare}
                            className="w-4 h-4 accent-[#F5A623] cursor-pointer"
                            title="Cocher pour comparer (2 à 3 candidats)"
                            data-testid={`compare-check-${a.id}`}
                        />
                    )}
                    {a.candidate?.has_portrait && a.candidate?.user_id && (
                        <ProfilePhoto candidateUserId={a.candidate.user_id} size={44} />
                    )}
                    <div className="min-w-0">
                        <div className="font-semibold text-sm truncate flex items-center gap-2 flex-wrap">
                            {a.candidate?.first_name} {a.candidate?.last_name}
                            {a.candidate?.is_super_talent && <SuperTalentBadge size="xs" />}
                            {a.compatibility_score != null && (
                                <CompatScore
                                    score={a.compatibility_score}
                                    testId={`compat-score-${a.id}`}
                                />
                            )}
                            {a.candidate?.rating != null && a.candidate?.rating_count > 0 && (
                                <span
                                    className="inline-flex items-center gap-1 text-[11px] font-bold text-[#F5A623]"
                                    data-testid={`candidate-rating-${a.id}`}
                                    title={`Note moyenne sur ${a.candidate.rating_count} évaluation${a.candidate.rating_count > 1 ? "s" : ""}`}
                                >
                                    ★ {a.candidate.rating.toFixed(1)}
                                    <span className="text-slate-400 font-normal">({a.candidate.rating_count})</span>
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-slate-500 truncate flex items-center gap-2 flex-wrap">
                            <span>{a.candidate?.city} · {a.candidate?.experience_years} an{a.candidate?.experience_years > 1 ? "s" : ""} d'exp.</span>
                            {a.distance_km != null && (
                                <span
                                    className="inline-flex items-center gap-0.5 text-[11px] text-[#0B192C] bg-white border border-slate-200 rounded-full px-2 py-0.5"
                                    data-testid={`app-distance-${a.id}`}
                                >
                                    📍 {a.distance_km.toFixed(1)} km
                                </span>
                            )}
                        </div>
                        {a.candidate?.badges?.length > 0 && (
                            <div className="mt-1.5">
                                <CandidateBadges badges={a.candidate.badges} size="xs" />
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    <span className={`pill ${
                        isSelected ? "pill-green" :
                        isRejected ? "pill-red" : "pill-slate"
                    }`}>
                        {STATUS_FR[a.status] || a.status}
                    </span>
                    {!isRejected && a.candidate?.user_id && (
                        <button
                            onClick={toggleFavorite}
                            className={`w-8 h-8 rounded-full flex items-center justify-center border transition ${
                                isFav
                                    ? "bg-[#F5A623]/15 border-[#F5A623] text-[#F5A623]"
                                    : "bg-white border-slate-200 text-slate-400 hover:text-[#F5A623] hover:border-[#F5A623]"
                            }`}
                            title={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
                            data-testid={`toggle-fav-${a.id}`}
                        >
                            <Heart size={14} className={isFav ? "fill-[#F5A623]" : ""} />
                        </button>
                    )}
                    {!hasSelected && !isSelected && !isRejected && (
                        <button
                            onClick={pickCandidate}
                            disabled={selecting}
                            className="btn-gold text-xs disabled:opacity-50"
                            data-testid={`client-select-candidate-${a.id}`}
                        >
                            {selecting ? "Sélection…" : "Choisir ce candidat"}
                        </button>
                    )}
                    {isSelected && (
                        <>
                            <button
                                onClick={downloadRecap}
                                className="btn-gold text-xs"
                                data-testid={`client-download-recap-${a.id}`}
                            >
                                Dossier prestataire (PDF)
                            </button>
                            <button
                                onClick={() => setShowDocs((v) => !v)}
                                className="btn-ghost-navy text-xs"
                                data-testid={`client-view-docs-${a.id}`}
                            >
                                {showDocs ? "Masquer" : "Documents"}
                            </button>
                            <button
                                onClick={() => setShowChat((v) => !v)}
                                className="btn-ghost-navy text-xs"
                                data-testid={`client-toggle-chat-${a.id}`}
                            >
                                💬 {showChat ? "Fermer" : "Messagerie"}
                            </button>
                            <button
                                onClick={async () => {
                                    const reason = window.prompt(
                                        "⚠️ Signaler un no-show\n\nLe candidat sera pénalisé automatiquement (1er = avertissement, 2ᵉ = suspension). Motif :",
                                        "Non-présentation le jour J",
                                    );
                                    if (!reason) return;
                                    try {
                                        await api.post(`/applications/${a.id}/no-show`, { reason });
                                        toast.success("No-show enregistré — pénalité appliquée");
                                        onSelected && onSelected();
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Erreur");
                                    }
                                }}
                                className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors inline-flex items-center gap-1"
                                title="Signaler que le candidat ne s'est pas présenté"
                                data-testid={`client-no-show-${a.id}`}
                            >
                                <UserX size={13} /> No-show
                            </button>
                        </>
                    )}
                </div>
            </div>
            {error && (
                <div className="mt-2 text-xs text-red-600" data-testid={`client-select-error-${a.id}`}>
                    {error}
                </div>
            )}
            {showDocs && isSelected && (
                <div className="mt-3 pl-3 border-l-2 border-[#F5A623]/40 space-y-1.5">
                    {docs.length === 0 && (
                        <div className="text-xs text-slate-400 py-1">
                            Aucun document déposé par ce candidat.
                        </div>
                    )}
                    {docs.map((d) => (
                        <div key={d.id} className="bg-white rounded px-3 py-2 flex items-center gap-2 flex-wrap border border-slate-200">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold truncate">{d.label || d.doc_type}</div>
                                <div className="text-[10px] text-slate-500 truncate">
                                    {d.original_filename} · {(d.size / 1024).toFixed(0)} Ko
                                </div>
                            </div>
                            <span className={`pill text-[10px] ${
                                d.status === "verified" ? "pill-green" :
                                d.status === "rejected" ? "pill-red" : "pill-gold"
                            }`}>
                                {d.status}
                            </span>
                            <button
                                onClick={() => downloadDoc(d)}
                                className="text-[11px] text-[#0B192C] hover:text-[#F5A623] font-semibold"
                                data-testid={`client-download-doc-${d.id}`}
                            >
                                Télécharger
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {showChat && isSelected && (
                <div className="mt-3">
                    <MissionChat
                        missionId={a.mission_id}
                        myRole="client"
                        headerLabel="Messagerie mission"
                        otherPartyName={`${a.candidate?.first_name || ""} ${a.candidate?.last_name || ""}`.trim()}
                    />
                </div>
            )}
        </div>
    );
}

function KPI({ label, value }) {
    return (
        <div className="sd-card p-5">
            <div className="overline mb-2">{label}</div>
            <div className="kpi-value">{value}</div>
        </div>
    );
}

function ClientContractsList() {
    const [contracts, setContracts] = useState([]);
    const STATUS_LABEL = {
        pending_signature: { label: "À signer", cls: "pill pill-gold" },
        signed: { label: "Signé", cls: "pill pill-green" },
        cancelled: { label: "Annulé", cls: "pill pill-red" },
        blocked: { label: "Bloqué", cls: "pill pill-red" },
    };
    useEffect(() => {
        api.get("/contracts").then((r) => setContracts(r.data)).catch(() => setContracts([]));
    }, []);
    const download = async (id, num) => {
        try {
            const resp = await api.get(`/contracts/${id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url; a.download = `contrat-${num}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch (_) {}
    };
    if (contracts.length === 0) {
        return <div className="text-sm text-slate-400 text-center py-4">Aucun contrat signé pour l'instant.</div>;
    }
    return (
        <div className="divide-y divide-slate-100">
            {contracts.map((c) => {
                const meta = STATUS_LABEL[c.status] || STATUS_LABEL.pending_signature;
                return (
                    <div key={c.id} className="py-3 flex justify-between items-center gap-3 flex-wrap" data-testid={`client-contract-${c.id}`}>
                        <div>
                            <div className="font-semibold text-sm">{c.mission_snapshot?.title}</div>
                            <div className="text-xs text-slate-500">
                                <b>{c.contract_number}</b> · {c.mission_snapshot?.date} · {c.mission_snapshot?.city}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="font-display font-bold">{fmtEUR(c.amount_ht)} HT</span>
                            <span className={meta.cls}>{meta.label}</span>
                            {c.status === "signed" && (
                                <button
                                    onClick={() => download(c.id, c.contract_number)}
                                    className="btn-ghost-navy text-xs"
                                    data-testid={`client-download-contract-${c.id}`}
                                >
                                    PDF
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function PendingEvaluations() {
    const [items, setItems] = useState([]);
    const [current, setCurrent] = useState(null);
    const refresh = () => {
        api.get("/evaluations/pending")
            .then((r) => setItems(r.data))
            .catch(() => setItems([]));
    };
    useEffect(() => { refresh(); }, []);
    if (items.length === 0) return null;
    return (
        <section className="sd-card p-6 mb-8 border-l-4 border-[#F5A623]" data-testid="pending-evaluations">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                <div>
                    <div className="overline text-[#F5A623]">À évaluer</div>
                    <h2 className="font-display text-xl">
                        {items.length} prestation{items.length > 1 ? "s" : ""} en attente d'évaluation
                    </h2>
                </div>
            </div>
            <div className="divide-y divide-slate-100">
                {items.map((it) => (
                    <div
                        key={it.application_id}
                        className="py-3 flex justify-between items-center gap-3 flex-wrap"
                        data-testid={`pending-eval-${it.application_id}`}
                    >
                        <div>
                            <div className="font-semibold text-sm">{it.mission_title}</div>
                            <div className="text-xs text-slate-500">
                                {it.mission_date} · {it.mission_city} · {it.candidate?.first_name} {it.candidate?.last_name}
                            </div>
                        </div>
                        <button
                            onClick={() => setCurrent(it)}
                            className="btn-gold text-xs"
                            data-testid={`open-eval-${it.application_id}`}
                        >
                            Évaluer
                        </button>
                    </div>
                ))}
            </div>
            <EvaluationModal
                open={!!current}
                pending={current}
                onClose={() => setCurrent(null)}
                onSubmitted={refresh}
            />
        </section>
    );
}


const CLIENT_CRITERIA_LABELS = {
    welcome: "Accueil",
    brief_conformity: "Conformité au brief",
    schedule_respect: "Horaires respectés",
    payment: "Paiement",
    environment: "Environnement",
};
function PaymentMethodBanner() {
    const [hasPm, setHasPm] = useState(null);
    useEffect(() => {
        api.get("/company/payment-methods")
            .then((r) => setHasPm((r.data.methods || []).length > 0))
            .catch(() => setHasPm(false));
    }, []);
    if (hasPm !== false) return null;
    return (
        <section
            className="sd-card p-6 mb-8 border-l-4 border-red-500 bg-red-50/60"
            data-testid="pm-banner-dashboard"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                    <div className="overline text-red-700 mb-1">Action requise</div>
                    <h3 className="font-display text-xl text-red-900 mb-1">
                        Enregistrez votre RIB ou votre carte
                    </h3>
                    <p className="text-sm text-slate-700 leading-relaxed">
                        Aucun débit maintenant. Un mandat SEPA ou une carte est nécessaire
                        pour publier vos missions (prélèvement automatique tous les 10 jours
                        à la clôture du cycle).
                    </p>
                </div>
                <Link
                    to="/client/moyen-de-paiement"
                    className="btn-gold text-sm px-4 py-2.5 whitespace-nowrap inline-flex items-center gap-2"
                    data-testid="pm-banner-cta"
                >
                    <CreditCard size={14} />
                    Enregistrer maintenant
                </Link>
            </div>
        </section>
    );
}

function DeferredBillingCard() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/company/billing/current")
            .then((r) => setData(r.data))
            .catch(() => setData(null));
    }, []);
    if (!data) return null;
    const p = data.period || {};
    const missionsCount = (data.missions || []).length;
    const profile = data.billing_profile || {};
    const outstandingEur = ((profile.outstanding_cents || 0) / 100).toFixed(2);
    const creditEur = ((profile.credit_limit_cents || 0) / 100).toFixed(0);
    const debitDate = p.scheduled_debit_at
        ? new Date(p.scheduled_debit_at).toLocaleDateString("fr-FR", {
              day: "numeric", month: "long", year: "numeric",
          })
        : "—";
    const hasPM = profile.has_default_payment_method;
    return (
        <section
            className="sd-card p-6 mb-8 border-l-4 border-[#F5A623]"
            data-testid="deferred-billing-card"
        >
            <div className="flex justify-between items-start flex-wrap gap-4 mb-4">
                <div>
                    <div className="overline text-[#F5A623] mb-1 flex items-center gap-2">
                        <CreditCard size={12} /> Facturation groupée · cycle en cours
                    </div>
                    <h2 className="font-display text-xl">
                        Cycle {p.period_start} → {p.period_end}
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">
                        Prochain prélèvement automatique le <b>{debitDate}</b>
                    </p>
                </div>
                <div className="text-right">
                    <div className="overline">Encours à prélever</div>
                    <div className="font-display text-2xl text-[#0B192C]">
                        {outstandingEur} €
                    </div>
                    <div className="text-[11px] text-slate-400">
                        Plafond crédit {creditEur} €
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center text-sm mb-4">
                <div className="bg-slate-50 rounded-xl p-3">
                    <div className="overline">Missions du cycle</div>
                    <div className="font-semibold text-[#0B192C] text-lg">{missionsCount}</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                    <div className="overline">Total HT</div>
                    <div className="font-semibold text-[#0B192C] text-lg">
                        {(p.total_ht || 0).toFixed(2)} €
                    </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                    <div className="overline">Total TTC</div>
                    <div className="font-semibold text-[#0B192C] text-lg">
                        {(p.total_ttc || 0).toFixed(2)} €
                    </div>
                </div>
            </div>
            {!hasPM && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    <b>Action requise —</b> aucun moyen de paiement enregistré. Vous
                    pourrez publier vos missions dès aujourd'hui, mais un mandat SEPA
                    ou une carte devra être ajouté avant le {debitDate} pour éviter
                    tout blocage du cycle suivant.
                </div>
            )}
            <p className="text-xs text-slate-400 mt-3 leading-relaxed">
                Toutes vos missions signées sont regroupées sur ce cycle et prélevées
                automatiquement à la clôture (cycles 1-10, 11-20, 21-fin de mois). Les
                prestataires sont payés après encaissement de votre prélèvement (J+10 à J+12).
            </p>
        </section>
    );
}


function ClientRatingCard() {    const [agg, setAgg] = useState(null);
    useEffect(() => {
        api.get("/client/my-rating")
            .then((r) => setAgg(r.data))
            .catch(() => setAgg(null));
    }, []);
    if (!agg || !agg.count) return null;
    return (
        <section
            className="sd-card p-6 mb-8 border-l-4 border-[#F5A623]"
            data-testid="client-rating-card"
        >
            <div className="flex items-start gap-6 flex-wrap">
                <div className="text-center">
                    <div className="overline text-[#F5A623] mb-1">Votre score</div>
                    <div className="font-display text-4xl text-[#0B192C]">
                        {agg.average?.toFixed(1)}
                        <span className="text-lg text-slate-400">/5</span>
                    </div>
                    <div className="text-xs text-slate-500">
                        {agg.count} notation{agg.count > 1 ? "s" : ""} reçue{agg.count > 1 ? "s" : ""}
                    </div>
                    {agg.would_work_again_pct !== null && (
                        <div className="text-xs text-slate-500 mt-1">
                            {agg.would_work_again_pct}% des pros retravailleraient avec vous
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-[240px] grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(CLIENT_CRITERIA_LABELS).map(([k, label]) => (
                        <div key={k} className="flex justify-between items-center text-sm">
                            <span className="text-slate-600">{label}</span>
                            <span className="font-semibold text-[#0B192C]">
                                {agg.criteria_avg?.[k]?.toFixed(1) ?? "—"}
                                <span className="text-xs text-slate-400"> /5</span>
                            </span>
                        </div>
                    ))}
                </div>
            </div>
            <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                Note attribuée par les prestataires à l'issue de vos missions (accueil, brief, horaires, paiement, environnement).
                Un score élevé augmente vos taux de candidatures spontanées.
            </p>
        </section>
    );
}


function PendingTimesheets() {
    const [items, setItems] = useState([]);
    const [current, setCurrent] = useState(null);
    const refresh = () => {
        api.get("/timesheets/pending-client")
            .then((r) => setItems(r.data))
            .catch(() => setItems([]));
    };
    useEffect(() => { refresh(); }, []);
    if (items.length === 0) return null;
    return (
        <section className="sd-card p-6 mb-8 border-l-4 border-blue-500" data-testid="pending-timesheets">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
                <div>
                    <div className="overline text-blue-600">Relevés d'heures à valider</div>
                    <h2 className="font-display text-xl">
                        {items.length} relevé{items.length > 1 ? "s" : ""} en attente de validation
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">
                        La facture finale ne sera émise qu'après validation de vos heures.
                    </p>
                </div>
            </div>
            <div className="divide-y divide-slate-100">
                {items.map((t) => (
                    <div key={t.id} className="py-3 flex justify-between items-center gap-3 flex-wrap"
                         data-testid={`pending-ts-${t.id}`}>
                        <div>
                            <div className="font-semibold text-sm">{t.mission?.title}</div>
                            <div className="text-xs text-slate-500">
                                {t.mission?.date} · {t.candidate?.first_name} {t.candidate?.last_name} · déclaré {t.declared_hours?.toFixed(2)} h
                            </div>
                        </div>
                        <button onClick={() => setCurrent(t)}
                                className="btn-gold text-xs"
                                data-testid={`open-ts-${t.id}`}>
                            Examiner
                        </button>
                    </div>
                ))}
            </div>
            <ClientTimesheetValidationModal
                open={!!current}
                timesheet={current}
                onClose={() => setCurrent(null)}
                onDone={refresh}
            />
        </section>
    );
}

